import {PhoneNumber} from 'platform/components';
import z from 'zod';

export type PostEmployeeApiResponse =
  | /** status 200 Standard response */ ({
      employeeId?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type PostEmployeeApiArg = {
  body: {
    personalData?: {
      firstName: string;
      lastName: string;
    } | null;
    employmentData?: {
      startDate: string;
    } | null;
  } | null;
};
export type GetEmployeeSimilarSearchApiResponse =
  | /** status 200 Standard response */ ({
      employees?: ({
        id?: string | null;
        personalData?: {
          firstName: string;
          lastName: string;
        } | null;
        employmentData?: {
          startDate: string;
        } | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ undefined;
export type GetEmployeeSimilarSearchApiArg = {
  firstName: string | null;
  lastName?: string | null;
};
export type GetEmployeePersonalDataApiResponse =
  | /** status 200 Standard response */ ({
      id: string;
      personalData: {
        firstName: string;
        lastName: string;
        middleName?: string | null;
        titleBefore?: string | null;
        titleAfter?: string | null;
        genderKey?: string | null;
        identificationNumber?: string | null;
        birthdate?: string | null;
        fte?: number | null;
      };
      employmentData: {
        startDate: string;
        endDate?: string | null;
      };
      addressData?: {
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        postalCode?: string | null;
        province?: string | null;
        isInvalid?: boolean | null;
        countryCode?: string | null;
      } | null;
      contactData?: {
        phoneNumberWork?: PhoneNumber | null;
        phoneNumberPrivate?: PhoneNumber | null;
        emailWork?: string | null;
        emailPrivate?: string | null;
      } | null;
      note?: string | null;
      state: string;
      publicId?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type GetEmployeePersonalDataApiArg = {
  employeeId: string;
};
export type PatchEmployeePersonalDataApiResponse = unknown;
export type PatchEmployeePersonalDataApiArg = {
  employeeId: string;
  body: {
    id?: string | null;
    personalData: {
      firstName: string;
      lastName: string;
      middleName?: string | null;
      titleBefore?: string | null;
      titleAfter?: string | null;
      genderKey?: string | null;
      identificationNumber?: string | null;
      birthdate?: string | null;
      fte?: number | null;
    };
    employmentData: {
      startDate: string;
      endDate?: string | null;
    };
    addressData?: {
      addressLine1?: string | null;
      addressLine2?: string | null;
      city?: string | null;
      postalCode?: string | null;
      province?: string | null;
      isInvalid?: boolean | null;
      countryCode?: string | null;
    } | null;
    contactData?: {
      phoneNumberWork?: {
        number: string;
        prefix: string;
        countryCode: string;
      } | null;
      phoneNumberPrivate?: {
        number: string;
        prefix: string;
        countryCode: string;
      } | null;
      emailWork?: string | null;
      emailPrivate?: string | null;
    } | null;
    note?: string | null;
    publicId?: string | null;
  } | null;
};
export type PutEmployeeActionApiResponse = unknown;
export type PutEmployeeActionApiArg = {
  employeeId: string;
  body: {
    actionKey: 'activate' | 'deactivate';
  } | null;
};
export type GetEmployeeActionsApiResponse =
  | /** status 200 Standard response */ ({
      employeeActions?: {
        optionClick?: 'activate' | 'deactivate' | null;
        options?: ({
          type: string | null;
          key: string | null;
          style: string | null;
          position: number | null;
        } | null)[];
      } | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type GetEmployeeActionsApiArg = {
  employeeId: string;
};
export type GetEmployeeOrgranisationalDataApiResponse =
  | /** status 200 Standard response */ ({
      id?: string | null;
      organisationalData?: {
        companyId: string;
        branches?: ({
          branchId: string;
          professionId?: string;
          workshopData?: {
            workTeamId?: string;
            hourlyRate?: {
              amount: number;
              currency: string;
            } | null;
            performancePercentage?: number | null;
            costCenterId?: string;
          } | null;
          authorizations?: ({
            authorizationProfileId: string;
            isToOffer: boolean;
            competencesIds?: string[];
          } | null)[];
        } | null)[];
      } | null;
      note?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type GetEmployeeOrgranisationalDataApiArg = {
  employeeId: string;
};

export type GetAuthorizationProfilesApiArg = {
  employeeId?: string;
  userId: string;
  companyId?: string;
  branchId: string;
};
export type GetAuthorizationProfilesApiBody = {
  authorizationProfileId: string;
  branchId: string;
  authorizationId: string;
  name: string;
  dealerNo: string | null;
  isDefault: boolean;
  isActive: boolean;
  companyId: string | null;
  companyAddressId: string | null;
  validFrom: string | null;
  validUntil: string | null;
};

export type GetAuthorizationProfilesApiResponse = GetAuthorizationProfilesApiBody[];

export type PatchEmployeeOrganisationalDataApiResponse = unknown;
export type PatchEmployeeOrganisationalDataApiArg = {
  employeeId: string;
  body: {
    organisationalData?: {
      companyId: string;
      branches?: ({
        branchId: string;
        professionId?: string;
        workshopData?: {
          workTeamId?: string;
          hourlyRate?: {
            amount: number;
            currency: string;
          } | null;
          performancePercentage?: number | null;
          costCenterId?: string;
        } | null;
        authorizations?: ({
          authorizationProfileId: string;
          isToOffer: boolean;
          competencesIds?: string[];
        } | null)[];
      } | null)[];
    } | null;
    note?: string | null;
  } | null;
};

export type PutEmployeeLinkApiResponse = /** status 204 Standard response */ undefined;
export type PutEmployeeLinkApiArg = {
  employeeId: string;
  userId: string;
};

export type GetEmployeeAuthorizationIntegrationApiArg = {
  userId: string;
};

export type EmployeeAuthorizationIntegrationBody = {
  integrationName: string;
  integrationCode:
    | 'DAIMLER_CLARA'
    | 'MERCEDES_XENTRY_JOB'
    | 'SAG'
    | 'TECRMI'
    | 'DAIMLER_XENTRY_JOB'
    | 'MAZDA_PORTAL'
    | 'INTERCARS_CZ';
  authorizationProfileId: string;
  authorizationProfileName: string;
  authorizationId: string;
  authorizationName: string;
  branchId: string;
  vehicleMakes: string | null;
  vehicleTypes: string | null;
};

export type GetEmployeeAuthorizationIntegrationApiResponse = EmployeeAuthorizationIntegrationBody[];

export type GetEmployeeLinkApiResponse = ({
  employeeId: string;
  contextTarget: 'USER';
  contextId: string;
} | null)[];

export type GetEmployeeLinkApiArg = {
  employeeId?: string;
  contextTarget?: 'USER' | 'PLANNER';
  contextId?: string;
};

export type GetEmployeeWorksListApiResponse =
  | /** status 200 Standard response */ ({
      s?: ({
        id?: string | null;
        description?: string | null;
        isActive?: boolean | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ undefined;
export type GetEmployeeIntegrationApiResponse =
  | {employeeId: string; contextTarget: 'PLANNER' | 'USER'; contextId: string}[]
  | null;
export type GetEmployeeIntegrationApiArg = {
  employeeId?: string;
  contextTarget?: 'PLANNER';
  contextId?: string;
};
export type GetEmployeeIntegrationsApiResponse =
  | {id: string; label?: string | null; isActive: boolean}[]
  | null;
export type DeleteEmployeeLinkApiArg = {
  employeeId: string;
  contextTarget: 'PLANNER';
};
export type DeleteEmployeeLinkApiResponse = unknown;
export type PutEmployeePlannerLinkApiArg = {
  employeeId: string;
  contextTarget: 'PLANNER';
  contextId: string;
};
export type PutEmployeePlannerLinkApiResponse = unknown;
export type GetEmployeeOrgranisationalDataSalesApiApiResponse =
  | /** status 200 Standard response */ ({
      organisationalData?: {
        companyId: string;
        branches: ({
          branchId: string;
          professionId?: string | null;
          authorizations?: ({
            authorizationProfileId: string;
          } | null)[];
        } | null)[];
      } | null;
      note?: string | null;
    } | null)
  | /** status 204 Standard response */ void;
export type GetEmployeeOrgranisationalDataSalesApiApiArg = {
  employeeId: string;
};

export type PatchEmployeeOrganisationalDataSalesApiApiResponse =
  /** status 204 Standard response */ void;
export type PatchEmployeeOrganisationalDataSalesApiApiArg = {
  employeeId: string;
  body: {
    organisationalData: {
      companyId: string;
      branches?: {
        branchId: string;
        professionId?: string | null;
        authorizations: {
          authorizationProfileId?: string | null;
        }[];
      }[];
    };
    note?: string;
  };
};

export const GetEmployeeMechanicsApiArgSchema = z
  .object({
    authorizationProfileId: z.string().nullish(),
    employeeCompetenceId: z.string().nullish(),
    withToOffer: z.boolean().nullish(),
    isActive: z.boolean().nullish(),
  })
  .nullish();

export const EmployeeMechanicsSchema = z.object({
  employeeId: z.string(),
  name: z.string(),
  costCenterId: z.string(),
});

export const EmployeeMechanicsApiResponseArraySchema = z.array(EmployeeMechanicsSchema);

export type EmployeeMechanicsV2 = z.infer<typeof EmployeeMechanicsSchema>;
export type GetEmployeeMechanicsApiResponse = z.infer<
  typeof EmployeeMechanicsApiResponseArraySchema
>;

export type GetEmployeeMechanicsApiArg = z.infer<typeof GetEmployeeMechanicsApiArgSchema> | void;
